// Board + graph data: one snapshot, kept current by the kit (live/index.ts hands over the core's board on every
// change). Moves and drops are the core's intents (board.move, board.place): optimistic there, shown at once in the
// next snapshot and rolled back by the core if refused; a refusal's message shows here. A snapshot keeps the objects
// of unchanged entities, so React redraws only what changed.
import { create } from "zustand";
import { type Api, ApiError, api, type ColumnEdit, type Snapshot, type TaskStatus } from "@/api";
import type { LiveStatus } from "@hq-org/kit-wasm";
import { errorText, useAuth } from "./auth";

export type DataState = {
	snapshot: Snapshot | null;
	error: string | null;
	/** Live connection state ("off" without live). */
	live: LiveStatus;
	setLive: (s: LiveStatus) => void;
	setSnapshot: (s: Snapshot) => void;
	load: () => Promise<void>;
	/** Status only (buttons, keyboard); the card keeps its rank. */
	move: (id: string, status: TaskStatus) => Promise<boolean>;
	/** Drop: into column `status`, right after card `after` (null = top). The core writes the card's status + rank. */
	place: (id: string, status: TaskStatus, after: string | null) => Promise<boolean>;
	/** A column of a space: add, rename, recolour, move, hide (the core's board.column). */
	column: (edit: ColumnEdit) => Promise<boolean>;
};

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
/** `next` with the objects of `prev` wherever an entity didn't change. */
function share(prev: Snapshot | null, next: Snapshot): Snapshot {
	if (!prev) return next;
	const keep = <T extends { id: string }>(a: T[], b: T[]) => {
		const old = new Map(a.map((x) => [x.id, x]));
		return b.map((x) => {
			const o = old.get(x.id);
			return o && same(o, x) ? o : x;
		});
	};
	return { ...next, tasks: keep(prev.tasks, next.tasks), projects: keep(prev.projects, next.projects), persons: keep(prev.persons, next.persons), repos: keep(prev.repos, next.repos) };
}

export function createDataStore(client: Api, onUnauthorized: () => void) {
	return create<DataState>()((set, get) => {
		const fail = (e: unknown) => {
			if (e instanceof ApiError && e.unauthorized) onUnauthorized();
			set({ error: errorText(e) });
		};
		const run = async (p: Promise<void>) => {
			set({ error: null });
			try {
				await p;
				return true;
			} catch (e) {
				fail(e);
				return false;
			}
		};
		let inflight: Promise<void> | null = null;
		const loadNow = async () => {
			try {
				const s = await client.snapshot.load();
				set({ snapshot: share(get().snapshot, s), error: null });
			} catch (e) {
				fail(e);
			}
		};
		return {
			snapshot: null,
			error: null,
			live: "off",
			setLive: (live) => set({ live }),
			setSnapshot: (s) => set({ snapshot: share(get().snapshot, s), error: null }),
			// one load at a time: a call while one runs shares it
			load() {
				if (!inflight) inflight = loadNow().finally(() => (inflight = null));
				return inflight;
			},
			move: (id, status) => run(client.board.move(id, status)),
			place: (id, status, after) => run(client.board.place(id, status, after)),
			column: (edit) => run(client.board.column(edit)),
		};
	});
}

export const useData = createDataStore(api, () => useAuth.getState().signOut());
